import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LoadingProvider } from './contexts/LoadingContext';
import { AuthModalProvider } from './contexts/AuthModalContext';
import { AuthProvider } from './auth/useAuth.jsx';
import ProtectedRoute from './route/ProtectedRoute';
import { ConfigProvider, App as AntdApp,theme } from 'antd';
import zhCN from 'antd/es/locale/zh_CN';

import 'dayjs/locale/zh-cn';
import 'tdesign-react/es/style/index.css';

// 路由懒加载
const MainPage = lazy(() => import('./pages/MainPage'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const User = lazy(() => import('./pages/User/User'));
const Overview = lazy(() => import('./pages/User/Overview/Overview'));
// const Management = lazy(() => import('./pages/User/Management/Management'));
const Account = lazy(() => import('./pages/User/Account/Account'));
const Billing = lazy(() => import('./pages/User/Billing/Billing'));
const InvitationRewards = lazy(() => import('./pages/User/InvitationRewards/InvitationRewards'));
// const Invoice = lazy(() => import('./pages/User/Invoice/Invoice'));
const Upgrade = lazy(() => import('./pages/Upgrade'));
const Product = lazy(() => import('./pages/Product'));
const Download = lazy(() => import('./pages/Download'));
const ProductPricing = lazy(() => import('./pages/Foot/ProductPricing/ProductPricing'));
const Faq = lazy(() => import('./pages/Foot/Faq/Faq'));
const AboutUs = lazy(() => import('./pages/Foot/AboutUs/AboutUs'));

// 标题映射表（值只需写后缀部分，"Moldia - " 由 AppTitle 统一拼接）
const TITLE_MAP = {
    '/': '世界模型',
    '/login': '登录',
    '/register': '注册',
    '/forgot-password': '忘记密码',
    '/download': '下载',
    '/product': '产品',
    '/upgrade': '升级',
    '/user': '用户中心',
    '/user/overview': '总览',
    // '/user/management': '套餐管理',
    '/user/account': '账户设置',
    '/user/billing': '积分流水',
    '/user/invitation-rewards': '邀请奖励',
    '/product-pricing': '产品定价',
    '/faq': '常见问题',
    '/about-us': '关于我们',
};

function AppTitle() {
  const location = useLocation();
  useEffect(() => {
    const title = TITLE_MAP[location.pathname]
      // 尝试匹配 /user/xxx 这类子路由
      || TITLE_MAP['/' + location.pathname.split('/').slice(1, 3).join('/')]
      || '世界模型';
    document.title = `Moldia - ${title}`;
  }, [location]);

  // /user 下的路由设置 #root 最小宽度，其他页面不限制
  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    if (location.pathname.startsWith('/user')) {
      root.style.minWidth = '1400px';
    } else {
      root.style.minWidth = '';
    }
  }, [location]);

  return null;
}


function App() {
    return (
        <ConfigProvider
            locale={zhCN}
            theme={{
                algorithm: theme.darkAlgorithm, 
                token: {
                    colorPrimary: '#61EA3C', // 你的绿色主题色
                },
                components: {
                    DatePicker: {
                        // 修改展开日历浮层的背景色（例如设置为深色背景 #141414）
                        colorBgElevated: '#141414', 
                        // 如果需要，也可以同时修改输入框本身的背景色
                        colorBgContainer: '#1f1f1f',
                        // 调整文字颜色以确保在深色背景下看清
                        colorText: '#e3e3e3',
                        colorTextHeading: '#ffffff', // 星期几和月份的标题颜色
                        colorTextDescription: '#595959', // 非本月日期的颜色
                        cellActiveWithRangeBg: '#61EA3C0D',
                    },
                },
            }}
        >
            <AntdApp>
                <AuthProvider>
                    <LoadingProvider>
                        <BrowserRouter
                            future={{
                                v7_startTransition: true,
                                v7_relativeSplatPath: true,
                            }}
                        >
                            <AppTitle />
                            <AuthModalProvider>
                            <Suspense fallback={null}>
                            <Routes>
                                <Route path="/" element={<MainPage />} />
                                <Route path="/login" element={<Login />} />
                                <Route path="/register" element={<Register />} />
                                <Route path="/forgot-password" element={<ForgotPassword />} />
                                <Route
                                    path="/user"
                                    element={
                                        <ProtectedRoute>
                                            <User />
                                        </ProtectedRoute>
                                    }
                                >
                                    <Route index element={<Navigate to="overview" replace />} />
                                    <Route path="overview" element={<Overview />} />
                                    {/* <Route path="management" element={<Management />} /> */}
                                    <Route path="account" element={<Account />} />
                                    <Route path="billing" element={<Billing />} />
                                    <Route path="invitation-rewards" element={<InvitationRewards />} />
                                    {/* <Route path="invoice" element={<Invoice />} /> */}
                                </Route>
                                <Route
                                    path="/upgrade"
                                    element={
                                        <ProtectedRoute>
                                            <Upgrade />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route path="/product" element={<Product />} />
                                <Route path="/download" element={<Download />} />
                                <Route path="/product-pricing" element={<ProductPricing />} />
                                <Route path="/faq" element={<Faq />} />
                                <Route path="/about-us" element={<AboutUs />} />
                            </Routes>
                            </Suspense>
                            </AuthModalProvider>
                        </BrowserRouter>
                    </LoadingProvider>
                </AuthProvider>
            </AntdApp>
        </ConfigProvider>
    );
}

export default App;